/**
 * ============================================================
 * OPERATIONAL HEALTH DASHBOARD
 * ============================================================
 * 
 * Internal-only dashboard for platform operational observability.
 * Shows: connection health, API health, upload health, operational metrics, errors.
 * 
 * This is NOT executive analytics - it's operational infrastructure monitoring.
 */

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RefreshCw,
  Wifi,
  WifiOff,
  Activity,
  Upload,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Briefcase,
  MessageSquare,
  TrendingUp,
  Server,
  Zap,
  Radio
} from 'lucide-react';
import { useWebSocketStatus, WS_STATES } from '@/services/useWebSocketStatus';
import wsManager from '@/services/websocketManager';
import { PHSCommandLayout } from '@/components/phs/enterprise';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// METRIC CARDS
// ============================================================

const MetricCard = memo(function MetricCard({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  color = 'bg-slate-500',
  trend,
  status 
}) {
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        {status && (
          <Badge className={`text-[10px] ${
            status === 'healthy' ? 'bg-emerald-500/20 text-emerald-400' :
            status === 'warning' ? 'bg-amber-500/20 text-amber-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {status}
          </Badge>
        )}
      </div>
      <div className="text-2xl font-bold text-white mb-1">
        {value ?? '-'}
      </div>
      <div className="text-xs text-slate-400">{title}</div>
      {subtitle && (
        <div className="text-[10px] text-slate-500 mt-1">{subtitle}</div>
      )}
      {trend && (
        <div className={`text-[10px] mt-1 flex items-center gap-1 ${
          trend > 0 ? 'text-emerald-400' : trend < 0 ? 'text-red-400' : 'text-slate-500'
        }`}>
          <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
          {Math.abs(trend)}% vs yesterday
        </div>
      )}
    </div>
  );
});

// ============================================================
// CONNECTION STATUS PANEL
// ============================================================

const ConnectionHealthPanel = memo(function ConnectionHealthPanel() {
  const { state, isConnected, isPolling, metrics } = useWebSocketStatus();
  
  const statusConfig = useMemo(() => {
    switch (state) {
      case WS_STATES.CONNECTED:
        return { label: 'Connected', color: 'bg-emerald-500', icon: Wifi, status: 'healthy' };
      case WS_STATES.RECONNECTING:
        return { label: 'Reconnecting', color: 'bg-amber-500', icon: Radio, status: 'warning' };
      case WS_STATES.FALLBACK_POLLING:
        return { label: 'Polling Fallback', color: 'bg-blue-500', icon: RefreshCw, status: 'warning' };
      case WS_STATES.OFFLINE:
        return { label: 'Offline', color: 'bg-red-500', icon: WifiOff, status: 'error' };
      default:
        return { label: 'Disconnected', color: 'bg-slate-500', icon: WifiOff, status: 'error' };
    }
  }, [state]);
  
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Wifi className="h-4 w-4 text-slate-400" />
          Connection Health
        </h3>
        <Badge className={`${statusConfig.color} text-white text-[10px]`}>
          {statusConfig.label}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-900/50 rounded-lg p-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Status</div>
          <div className="flex items-center gap-2">
            <statusConfig.icon className={`h-4 w-4 ${
              statusConfig.status === 'healthy' ? 'text-emerald-400' :
              statusConfig.status === 'warning' ? 'text-amber-400' : 'text-red-400'
            }`} />
            <span className="text-sm text-white">{statusConfig.label}</span>
          </div>
        </div>
        
        <div className="bg-slate-900/50 rounded-lg p-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Reconnects</div>
          <div className="text-sm text-white">{metrics.reconnectCount || 0}</div>
        </div>
        
        <div className="bg-slate-900/50 rounded-lg p-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Messages</div>
          <div className="text-sm text-white">{metrics.messageCount || 0}</div>
        </div>
        
        <div className="bg-slate-900/50 rounded-lg p-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Polling Fallbacks</div>
          <div className="text-sm text-white">{metrics.pollingFallbackCount || 0}</div>
        </div>
      </div>
      
      {metrics.lastConnectTime && (
        <div className="mt-3 text-[10px] text-slate-500">
          Session uptime: {Math.round(metrics.sessionUptime / 1000 / 60)} min
        </div>
      )}
    </div>
  );
});

// ============================================================
// ERROR LIST PANEL
// ============================================================

const ErrorListPanel = memo(function ErrorListPanel({ errors = [], loading }) {
  if (loading) {
    return (
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <Skeleton className="h-4 w-32 mb-4 bg-slate-700" />
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-12 w-full bg-slate-700" />
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-400" />
          Recent Errors
        </h3>
        <Badge className="bg-red-500/20 text-red-400 text-[10px]">
          {errors.length} total
        </Badge>
      </div>
      
      {errors.length === 0 ? (
        <div className="text-center py-6 text-slate-500 text-sm">
          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500/50" />
          No recent errors
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {errors.slice(0, 10).map((error, idx) => (
            <div key={error.error_id || idx} className="bg-slate-900/50 rounded-lg p-2.5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white font-medium truncate">
                    {error.component || 'Unknown'}
                  </div>
                  <div className="text-[10px] text-slate-400 truncate">
                    {error.error_message || 'No message'}
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 flex-shrink-0">
                  {new Date(error.created_at).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// ============================================================
// MAIN DASHBOARD
// ============================================================

function OperationalHealthDashboard() {
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [auditSummary, setAuditSummary] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [errors, setErrors] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [healthRes, auditRes, perfRes, errorRes] = await Promise.all([
        axios.get(`${API}/api/phs/health/operational`, { headers }),
        axios.get(`${API}/api/phs/audit/summary?days=1`, { headers }),
        axios.get(`${API}/api/phs/metrics/performance/summary?hours=24`, { headers }),
        axios.get(`${API}/api/phs/audit/errors?days=1&limit=20`, { headers })
      ]);
      
      setHealthData(healthRes.data);
      setAuditSummary(auditRes.data);
      setPerformanceMetrics(perfRes.data);
      setErrors(errorRes.data.errors || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch health data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);
  
  const summary = auditSummary?.summary || {};
  const health = healthData?.health || {};
  const operations = healthData?.operations || {};
  const errorsData = healthData?.errors || {};
  
  return (
    <PHSCommandLayout>
      <div className="min-h-full bg-slate-900 p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">Operational Health</h1>
            <p className="text-sm text-slate-400">Platform monitoring & observability</p>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-[10px] text-slate-500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              disabled={loading}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        
        {/* System Status Banner */}
        <div className={`mb-6 p-4 rounded-xl border ${
          health.status === 'healthy' 
            ? 'bg-emerald-500/10 border-emerald-500/30' 
            : 'bg-amber-500/10 border-amber-500/30'
        }`}>
          <div className="flex items-center gap-3">
            {health.status === 'healthy' ? (
              <CheckCircle className="h-5 w-5 text-emerald-400" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-400" />
            )}
            <div>
              <div className={`text-sm font-medium ${
                health.status === 'healthy' ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                System Status: {health.status === 'healthy' ? 'Healthy' : 'Degraded'}
              </div>
              <div className="text-[10px] text-slate-400">
                API: {health.api_responding ? 'Responding' : 'Not responding'} • 
                Database: {health.database_connected ? 'Connected' : 'Disconnected'}
              </div>
            </div>
          </div>
        </div>
        
        {/* Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricCard
            title="Errors (24h)"
            value={errorsData.last_24h || 0}
            subtitle={`${errorsData.last_hour || 0} in last hour`}
            icon={AlertTriangle}
            color={errorsData.last_hour > 5 ? 'bg-red-500' : 'bg-emerald-500'}
            status={errorsData.last_hour > 5 ? 'warning' : 'healthy'}
          />
          
          <MetricCard
            title="Overdue Jobs"
            value={operations.overdue_jobs || 0}
            icon={Clock}
            color={operations.overdue_jobs > 10 ? 'bg-red-500' : 'bg-blue-500'}
            status={operations.overdue_jobs > 10 ? 'warning' : 'healthy'}
          />
          
          <MetricCard
            title="Pending Reviews"
            value={operations.pending_reviews || 0}
            icon={Briefcase}
            color="bg-purple-500"
          />
          
          <MetricCard
            title="Upload Failures"
            value={operations.upload_failures_24h || 0}
            icon={Upload}
            color={operations.upload_failures_24h > 5 ? 'bg-amber-500' : 'bg-teal-500'}
            status={operations.upload_failures_24h > 5 ? 'warning' : 'healthy'}
          />
        </div>
        
        {/* Activity Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricCard
            title="Audit Events (24h)"
            value={summary.total_events || 0}
            icon={Activity}
            color="bg-indigo-500"
          />
          
          <MetricCard
            title="Status Changes"
            value={summary.status_changes || 0}
            icon={Zap}
            color="bg-cyan-500"
          />
          
          <MetricCard
            title="Evidence Uploads"
            value={summary.evidence_uploads || 0}
            icon={Upload}
            color="bg-teal-500"
          />
          
          <MetricCard
            title="Blocked Invoices"
            value={summary.blocked_invoices || 0}
            icon={XCircle}
            color={summary.blocked_invoices > 0 ? 'bg-amber-500' : 'bg-slate-500'}
          />
        </div>
        
        {/* Detail Panels */}
        <div className="grid md:grid-cols-2 gap-4">
          <ConnectionHealthPanel />
          <ErrorListPanel errors={errors} loading={loading} />
        </div>
        
        {/* Performance Metrics */}
        {performanceMetrics?.metrics_by_type && Object.keys(performanceMetrics.metrics_by_type).length > 0 && (
          <div className="mt-6 bg-slate-800 rounded-xl p-4 border border-slate-700">
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Server className="h-4 w-4 text-slate-400" />
              Performance Metrics (24h)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(performanceMetrics.metrics_by_type).map(([type, data]) => (
                <div key={type} className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">
                    {type.replace(/_/g, ' ')}
                  </div>
                  <div className="text-sm text-white font-medium">
                    {data.avg_duration_ms}ms avg
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {data.count} samples • max {data.max_duration_ms}ms
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(OperationalHealthDashboard, 'admin');
